import React from 'react';
import { Outlet, Link } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Container,
  useTheme,
  useMediaQuery,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Menu as MenuIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(8px)',
  boxShadow: 'none',
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1, 2),
  textTransform: 'none',
  fontSize: '1rem',
  fontWeight: 500,
}));

const GradientButton = styled(StyledButton)(({ theme }) => ({
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  color: 'white',
  '&:hover': {
    background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
  },
}));

const PublicLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = React.useState(null);
  const { user } = useSelector((state) => state.auth);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <StyledAppBar position="fixed">
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            <Typography
              variant="h6"
              component={Link}
              to="/"
              sx={{
                flexGrow: 1,
                textDecoration: 'none',
                fontWeight: 700,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              PsychePoint
            </Typography>

            {isMobile ? (
              <>
                <IconButton
                  color="primary"
                  aria-label="menu"
                  onClick={handleMenuOpen}
                  sx={{ ml: 2 }}
                >
                  <MenuIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  PaperProps={{
                    elevation: 0,
                    sx: {
                      mt: 1.5,
                      overflow: 'visible',
                      filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                      '&:before': {
                        content: '""',
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        right: 14,
                        width: 10,
                        height: 10,
                        bgcolor: 'background.paper',
                        transform: 'translateY(-50%) rotate(45deg)',
                        zIndex: 0,
                      },
                    },
                  }}
                >
                  <MenuItem component={Link} to="/book-appointment" onClick={handleMenuClose}>
                    Book Appointment
                  </MenuItem>
                  {!user && (
                    <>
                      <MenuItem component={Link} to="/login" onClick={handleMenuClose}>
                        Login
                      </MenuItem>
                      <MenuItem component={Link} to="/signup" onClick={handleMenuClose}>
                        Sign Up
                      </MenuItem>
                    </>
                  )}
                  {user && (
                    <MenuItem
                      component={Link}
                      to={`/${user.role}-dashboard`}
                      onClick={handleMenuClose}
                    >
                      Dashboard
                    </MenuItem>
                  )}
                </Menu>
              </>
            ) : (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <StyledButton
                  color="primary"
                  component={Link}
                  to="/book-appointment"
                >
                  Book Appointment
                </StyledButton>
                {!user ? (
                  <>
                    <StyledButton
                      color="primary"
                      component={Link}
                      to="/login"
                    >
                      Login
                    </StyledButton>
                    <GradientButton
                      component={Link}
                      to="/signup"
                    >
                      Sign Up
                    </GradientButton>
                  </>
                ) : (
                  <GradientButton
                    component={Link}
                    to={`/${user.role}-dashboard`}
                  >
                    Dashboard
                  </GradientButton>
                )}
              </Box>
            )}
          </Toolbar>
        </Container>
      </StyledAppBar>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: { xs: 8, sm: 9 },
          minHeight: '100vh',
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Container maxWidth="xl">
          <Outlet />
        </Container>
      </Box>
    </Box>
  );
};

export default PublicLayout; 